/**
 * useOttoDog
 *
 * Three.js composable that renders an animated 3D dog character (Otto).
 * Loads a .glb model from /models/otto-dog.glb and exposes animation controls
 * driven by agent state (idle, tail wag, head tilt, talking, ear perk).
 *
 * Two camera modes:
 *  - 'fab'  — zoomed into head, small canvas (48×48)
 *  - 'full' — shows full body, larger canvas
 */

import { ref, onBeforeUnmount, watch, type Ref } from 'vue';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { playOttoSound, stopAllOttoSounds, type OttoSound } from '../utils/ottoSounds';

export type OttoAnimation = 'idle' | 'wagTail' | 'tiltHead' | 'talk' | 'perkEars' | 'listen';
export type OttoCameraMode = 'fab' | 'full';

const MODEL_PATH = '/models/otto-dog.glb';
const CROSSFADE_DURATION = 0.35; // seconds

// Module-level GLTF cache — loaded once, reused across all instances/remounts
let _cachedGltf: GLTF | null = null;
let _cachePromise: Promise<GLTF> | null = null;

function getOrLoadGltf(): Promise<GLTF> {
  if (_cachedGltf) return Promise.resolve(_cachedGltf);
  if (_cachePromise) return _cachePromise;
  _cachePromise = new Promise<GLTF>((resolve, reject) => {
    new GLTFLoader().load(
      MODEL_PATH,
      (gltf) => {
        _cachedGltf = gltf;
        resolve(gltf);
      },
      undefined,
      reject,
    );
  });
  return _cachePromise;
}

export function useOttoDog(getContainer: () => HTMLElement | null, mode: Ref<OttoCameraMode>) {
  // ── Reactive state ──────────────────────────────────────────────
  const isLoaded = ref(false);
  const isLoading = ref(false);
  const loadError = ref<string | null>(null);
  const currentAnimation = ref<OttoAnimation>('idle');
  const availableClips = ref<string[]>([]);

  // ── Three.js objects (non-reactive) ─────────────────────────────
  let scene: THREE.Scene;
  let camera: THREE.PerspectiveCamera;
  let renderer: THREE.WebGLRenderer;
  let _lastTime = 0;
  let _elapsed = 0;
  let mixer: THREE.AnimationMixer | null = null;
  let animationId: number | null = null;
  let model: THREE.Group | null = null;
  let controls: OrbitControls | null = null;

  // WASD orbit state
  const heldOrbitKeys = new Set<string>();
  const _spherical = new THREE.Spherical();
  const ORBIT_SPEED = 1.8; // radians per second

  // Map of animation name → clip
  const clips = new Map<string, THREE.AnimationClip>();
  const actions = new Map<string, THREE.AnimationAction>();
  let activeAction: THREE.AnimationAction | null = null;

  // Bones for programmatic animation fallbacks
  let tailBone: THREE.Bone | null = null;
  let headBone: THREE.Bone | null = null;
  let jawBone: THREE.Bone | null = null;
  let earLBone: THREE.Bone | null = null;
  let earRBone: THREE.Bone | null = null;

  // Programmatic animation state
  let programmaticAnim: OttoAnimation = 'idle';
  let programmaticTime = 0;

  // Pending clip to play once model finishes loading
  let pendingClipName: string | null = null;

  // ── Initialization ──────────────────────────────────────────────
  function init() {
    const container = getContainer();
    if (!container) return;

    const w = container.clientWidth || 48;
    const h = container.clientHeight || 48;

    // Scene — transparent background
    scene = new THREE.Scene();

    // Camera
    camera = new THREE.PerspectiveCamera(35, w / h, 0.01, 100);
    positionCamera();

    // Renderer
    renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'low-power',
    });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Styling for the canvas
    renderer.domElement.style.borderRadius = 'inherit';
    renderer.domElement.style.display = 'block';

    // OrbitControls — full mode gets rotate + scroll-to-zoom
    if (mode.value === 'full') {
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.enableZoom = true;
      controls.zoomSpeed = 0.8;
      controls.minDistance = 0.8;
      controls.maxDistance = 6;
      controls.enablePan = false;
      controls.minPolarAngle = Math.PI / 6;
      controls.maxPolarAngle = Math.PI / 2.2;
      controls.autoRotate = false;
      controls.autoRotateSpeed = 0;
      controls.target.set(0, 0.5, 0);
    }

    // Lighting
    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambient);

    const key = new THREE.DirectionalLight(0xffffff, 1.2);
    key.position.set(3, 5, 4);
    scene.add(key);

    const fill = new THREE.DirectionalLight(0xb0c4de, 0.4);
    fill.position.set(-2, 3, -2);
    scene.add(fill);

    const rim = new THREE.DirectionalLight(0xffd700, 0.3);
    rim.position.set(0, 2, -5);
    scene.add(rim);

    const hemi = new THREE.HemisphereLight(0x87ceeb, 0x8b7355, 0.5);
    scene.add(hemi);

    _lastTime = 0;
    _elapsed = 0;
    _lastRenderStamp = 0;
    heldOrbitKeys.clear();

    // Load model
    loadModel();

    // Start render loop
    animate();
  }

  // ── Camera positioning ──────────────────────────────────────────
  function positionCamera() {
    if (mode.value === 'fab') {
      // Zoomed to head
      camera.position.set(0, 0.8, 1.5);
      camera.lookAt(0, 0.6, 0);
    } else {
      // Full body view
      camera.position.set(1.5, 1.2, 2.5);
      camera.lookAt(0, 0.5, 0);
    }
  }

  // ── Model loading ───────────────────────────────────────────────
  function loadModel() {
    isLoading.value = true;
    loadError.value = null;

    getOrLoadGltf()
      .then((gltf) => {
        // Use the original scene directly — cloning breaks animation bone references.
        // The FAB instance is hidden (v-show) while the full-body one is visible, so
        // there is no conflict between the two instances at runtime.
        model = gltf.scene;

        // Auto-scale: normalize model to ~1 unit tall
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 0) {
          const scale = 1.2 / maxDim;
          model.scale.setScalar(scale);
        }

        // Center horizontally, place feet at y=0
        const box2 = new THREE.Box3().setFromObject(model);
        const center = box2.getCenter(new THREE.Vector3());
        model.position.x -= center.x;
        model.position.z -= center.z;
        model.position.y -= box2.min.y;

        scene.add(model);

        // Setup animation mixer
        mixer = new THREE.AnimationMixer(model);

        // Store all clips
        for (const clip of gltf.animations) {
          clips.set(clip.name, clip);
          const action = mixer.clipAction(clip);
          actions.set(clip.name, action);
        }

        // Expose clip names reactively
        availableClips.value = Array.from(clips.keys());

        // Find named bones for programmatic animation fallback
        model.traverse((child) => {
          if (child instanceof THREE.Bone) {
            const name = child.name.toLowerCase();
            if (name.includes('tail')) tailBone = child;
            if (name.includes('head') && !headBone) headBone = child;
            if (name.includes('jaw') || name.includes('mouth')) jawBone = child;
            if (name.includes('ear')) {
              if (name.includes('l') || name.includes('left')) earLBone = child;
              else if (name.includes('r') || name.includes('right')) earRBone = child;
              else if (!earLBone) earLBone = child;
              else earRBone = child;
            }
          }
        });

        isLoaded.value = true;
        isLoading.value = false;

        // Play initial animation
        playAnimation(currentAnimation.value);

        // Play any clip that was requested before the model finished loading
        if (pendingClipName) {
          const pending = pendingClipName;
          pendingClipName = null;
          playClipByName(pending);
        }
      })
      .catch((error) => {
        console.error('[OttoDog] Failed to load model:', error);
        loadError.value = 'Failed to load 3D model';
        isLoading.value = false;
      });
  }

  // ── Animation playback ──────────────────────────────────────────
  function playAnimation(anim: OttoAnimation) {
    currentAnimation.value = anim;
    stopAllOttoSounds();

    // Try to find a matching clip from the model's built-in animations
    const clipName = findClipName(anim);

    if (clipName && actions.has(clipName)) {
      const newAction = actions.get(clipName)!;
      if (activeAction && activeAction !== newAction) {
        newAction.reset();
        newAction.play();
        activeAction.crossFadeTo(newAction, CROSSFADE_DURATION, true);
      } else if (!activeAction) {
        newAction.reset();
        newAction.play();
      }
      activeAction = newAction;
      programmaticAnim = 'idle'; // disable programmatic fallback
      // NOTE: sounds are only played on explicit user-triggered playClipByName calls,
      // not on reactive animation state changes (would play on mic press, etc.)
    } else {
      // No matching clip — use programmatic bone animation
      // Keep any current clip playing (idle) and overlay bone manipulation
      if (!activeAction && actions.size > 0) {
        // Play the first available clip as base
        const firstAction = actions.values().next().value;
        if (firstAction) {
          firstAction.reset();
          firstAction.play();
          activeAction = firstAction;
        }
      }
      programmaticAnim = anim;
      programmaticTime = 0;
    }
  }

  /** Map our animation names to common clip naming conventions */
  function findClipName(anim: OttoAnimation): string | null {
    const names = Array.from(clips.keys());
    if (names.length === 0) return null;

    // Common name patterns to look for per animation type
    const patterns: Record<OttoAnimation, string[]> = {
      idle: ['idlea', 'idle', 'rest_pose', 'stand', 'breathing', 'rest', 'default'],
      wagTail: ['bark', 'wag', 'tail', 'happy', 'excited', 'jump_inplace', 'walk_forward'],
      tiltHead: ['check', 'tilt', 'curious', 'look', 'think'],
      talk: ['bark', 'eat', 'talk', 'speak', 'mouth', 'howl', 'bite'],
      perkEars: ['check', 'sit_up', 'listen', 'ear', 'alert', 'attention'],
      listen: ['sitting', 'sit_side', 'sit', 'listen', 'ear', 'alert', 'attention'],
    };

    const searchTerms = patterns[anim] || ['idle'];

    for (const term of searchTerms) {
      const match = names.find((n) => n.toLowerCase().includes(term));
      if (match) return match;
    }

    // Fallback: for idle, use the first animation
    if (anim === 'idle' && names.length > 0) return names[0];

    return null;
  }

  // ── Programmatic bone animations (fallback) ─────────────────────
  function updateProgrammaticAnimation(dt: number) {
    if (programmaticAnim === 'idle') return;
    programmaticTime += dt;
    const t = programmaticTime;

    switch (programmaticAnim) {
      case 'wagTail':
        if (tailBone) {
          tailBone.rotation.z = Math.sin(t * 12) * 0.4;
          tailBone.rotation.y = Math.sin(t * 8) * 0.2;
        }
        break;

      case 'tiltHead':
        if (headBone) {
          const target = Math.sin(t * 0.8) * 0.2;
          headBone.rotation.z = THREE.MathUtils.lerp(headBone.rotation.z, target, 0.05);
        }
        break;

      case 'talk':
        if (jawBone) {
          jawBone.rotation.x = Math.abs(Math.sin(t * 10)) * 0.15;
        }
        if (tailBone) {
          tailBone.rotation.z = Math.sin(t * 6) * 0.2;
        }
        break;

      case 'perkEars':
      case 'listen':
        if (earLBone) {
          earLBone.rotation.x = THREE.MathUtils.lerp(earLBone.rotation.x, -0.3, 0.05);
        }
        if (earRBone) {
          earRBone.rotation.x = THREE.MathUtils.lerp(earRBone.rotation.x, -0.3, 0.05);
        }
        if (headBone) {
          headBone.rotation.z = Math.sin(t * 0.5) * 0.1;
        }
        break;
    }
  }

  // ── Idle micro-animations (always active for liveliness) ────────
  function updateIdleMicro(dt: number, elapsed: number) {
    if (!model) return;

    // Subtle breathing — gentle scale oscillation
    const breathe = 1 + Math.sin(elapsed * 1.5) * 0.005;
    model.scale.y = model.scale.x * breathe;

    // Subtle body sway
    model.rotation.y = Math.sin(elapsed * 0.3) * 0.02;
  }

  // ── Render loop ─────────────────────────────────────────────────
  /**
   * The FAB is decorative and on screen on every hub page. Idle, the dog only
   * breathes, and 30 fps reads the same as 60 while halving the WebGL work the
   * app pays for continuously — the pump that makes the other pages feel heavy
   * on a tablet. Anything that actually moves at display rate (a clip, an orbit
   * key) keeps the full frame rate.
   */
  const IDLE_FRAME_INTERVAL_MS = 1000 / 30;
  let _lastRenderStamp = 0;

  function animate(timestamp = 0) {
    animationId = requestAnimationFrame(animate);

    if (heldOrbitKeys.size === 0 && currentAnimation.value === 'idle') {
      if (_lastRenderStamp && timestamp >= _lastRenderStamp && timestamp - _lastRenderStamp < IDLE_FRAME_INTERVAL_MS) {
        return;
      }
    }
    _lastRenderStamp = timestamp;

    const now = timestamp / 1000;
    const dt = _lastTime ? Math.min(now - _lastTime, 0.1) : 0;
    _lastTime = now;
    _elapsed += dt;
    const elapsed = _elapsed;

    if (mixer) mixer.update(dt);

    // WASD orbit: rotate camera around target in spherical coords
    if (controls && heldOrbitKeys.size > 0) {
      const offset = camera.position.clone().sub(controls.target);
      _spherical.setFromVector3(offset);
      if (heldOrbitKeys.has('arrowleft')) _spherical.theta -= ORBIT_SPEED * dt;
      if (heldOrbitKeys.has('arrowright')) _spherical.theta += ORBIT_SPEED * dt;
      if (heldOrbitKeys.has('w')) _spherical.phi -= ORBIT_SPEED * dt;
      if (heldOrbitKeys.has('s')) _spherical.phi += ORBIT_SPEED * dt;
      _spherical.phi = Math.max(Math.PI / 6, Math.min(Math.PI / 2.2, _spherical.phi));
      _spherical.makeSafe();
      camera.position.copy(controls.target).add(new THREE.Vector3().setFromSpherical(_spherical));
      camera.lookAt(controls.target);
    }

    if (controls) controls.update();

    updateProgrammaticAnimation(dt);
    updateIdleMicro(dt, elapsed);

    renderer.render(scene, camera);
  }

  // ── Resize handling ─────────────────────────────────────────────
  function resize() {
    const container = getContainer();
    if (!container || !renderer) return;

    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;

    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }

  // ── Set animation from outside ──────────────────────────────────
  function setAnimation(anim: OttoAnimation) {
    if (anim === currentAnimation.value) return;
    if (!isLoaded.value) {
      currentAnimation.value = anim;
      return;
    }
    playAnimation(anim);
  }

  // ── Clip-name → sound mapping ─────────────────────────────────
  // One sound per animation clip (keyed by lowercase clip name).
  const clipSoundMap: Record<string, OttoSound> = {
    // Barking / vocals
    barking: 'bark',
    // Attacks
    attack_bite: 'bark_growl',
    attack_combat: 'bark_growl',
    attack_3: 'bark_growl',
    attack_hand: 'bark_growl',
    attack_fallback: 'yelp',
    // Hit / death
    hit: 'yelp',
    die: 'whine',
    die_2: 'whine',
    // Reborn / revive
    reborn: 'effort',
    relive: 'effort',
    // Falling
    falling: 'yelp',
    // Jump
    jump_inplace: 'effort',
    jump_rm: 'effort',
    // Sit / rest
    sitting: 'whine',
    sit_side_way: 'whine',
    sit_side_way_2: 'whine',
    sit_up: 'collar',
    // Check / eat
    check: 'sniff',
    eat: 'eat',
    // Walk
    walk_forward_inplace: 'footstep_walk',
    walk_backward_inplace: 'footstep_walk',
    walk_left_rm: 'footstep_walk',
    walk_right_rm: 'footstep_walk',
    // Run
    run_forward_inplace: 'footstep_run',
    run_backward_inplace: 'footstep_run',
    run_left_rm: 'footstep_run',
    run_right_rm: 'footstep_run',
    // Swim
    swim_forward_2: 'splash',
    swim_backward: 'splash',
    swim_left_rm: 'splash',
    swim_right_rm: 'splash',
    swim_all_rm: 'splash',
    swim_floating: 'pant',
  };

  function playSoundForClip(clipName: string) {
    const key = clipName.toLowerCase();
    const sound = clipSoundMap[key];
    if (sound) playOttoSound(sound);
  }

  /** Play a clip by its exact name (for action buttons) */
  function playClipByName(name: string) {
    if (!mixer || !actions.has(name)) {
      // Model not loaded yet — queue the clip so it plays as soon as it's ready
      pendingClipName = name;
      return;
    }
    pendingClipName = null;
    stopAllOttoSounds();
    const newAction = actions.get(name)!;
    if (activeAction && activeAction !== newAction) {
      newAction.reset();
      newAction.play();
      activeAction.crossFadeTo(newAction, CROSSFADE_DURATION, true);
    } else if (!activeAction) {
      newAction.reset();
      newAction.play();
    }
    activeAction = newAction;
    programmaticAnim = 'idle';
    playSoundForClip(name);
    // If the render loop was paused (element was hidden), kick it back on
    if (animationId == null) resume();
  }

  // ── Pause / resume (for performance when not visible) ───────────
  function pause() {
    if (animationId != null) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
  }

  function resume() {
    if (animationId == null && renderer) {
      _lastTime = 0; // reset so we don't get a huge jump on resume
      _lastRenderStamp = 0;
      animate();
    }
  }

  // ── Cleanup ─────────────────────────────────────────────────────
  function dispose() {
    pause();

    if (controls) {
      controls.dispose();
      controls = null;
    }

    if (mixer) {
      mixer.stopAllAction();
      mixer = null;
    }

    if (renderer) {
      renderer.dispose();
      const container = getContainer();
      if (container && renderer.domElement.parentElement === container) {
        container.removeChild(renderer.domElement);
      }
    }

    if (model) {
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach((m: THREE.Material) => m.dispose());
          } else if (child.material) {
            child.material.dispose();
          }
        }
      });
      model = null;
    }

    clips.clear();
    actions.clear();
    activeAction = null;
    isLoaded.value = false;
    availableClips.value = [];
    heldOrbitKeys.clear();
  }

  // ── WASD orbit control (called by keyboard composable) ──────────
  function orbitKey(key: string, pressed: boolean) {
    if (pressed) heldOrbitKeys.add(key);
    else heldOrbitKeys.delete(key);
  }

  function resetView() {
    positionCamera();
    if (controls) {
      controls.target.set(0, 0.5, 0);
      controls.update();
    }
  }

  /** Snap the dog model to face a given world-space azimuth (radians). */
  function setFacingAngle(angle: number) {
    if (model) model.rotation.y = angle;
  }

  // Watch mode changes to reposition camera
  watch(mode, () => {
    positionCamera();
    resize();
  });

  onBeforeUnmount(() => {
    dispose();
  });

  return {
    init,
    dispose,
    resize,
    pause,
    resume,
    setAnimation,
    playClipByName,
    orbitKey,
    resetView,
    setFacingAngle,
    isLoaded,
    isLoading,
    loadError,
    currentAnimation,
    availableClips,
  };
}
